import { useQuery } from '@tanstack/react-query'
import { CalendarCheck2, CalendarRange, MoreHorizontal, Pencil, Plus, Trash2, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { useDeleteYear, useSetCurrentYear, yearsQuery } from '@/features/academic/api'
import { DeleteDialog } from '@/features/academic/components/DeleteDialog'
import { YearDialog } from '@/features/academic/components/YearDialog'
import type { AcademicYear } from '@/features/academic/types'
import { Can } from '@/features/auth/guards'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDate, formatNumber } from '@/utils/format'

function yearProgress(year: AcademicYear): number {
  const start = new Date(`${year.startsOn}T00:00:00`).getTime()
  const end = new Date(`${year.endsOn}T00:00:00`).getTime()
  return Math.min(100, Math.max(0, ((Date.now() - start) / (end - start)) * 100))
}

function weeksBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / (7 * 86_400_000),
  )
}

export function YearsPage() {
  const { t } = useTranslation('academic')
  useDocumentTitle('Academic years')
  const years = useQuery(yearsQuery())
  const setCurrent = useSetCurrentYear()
  const remove = useDeleteYear()
  const [editing, setEditing] = useState<AcademicYear | 'new' | null>(null)
  const [deleting, setDeleting] = useState<AcademicYear | null>(null)

  const makeCurrent = (year: AcademicYear) =>
    setCurrent.mutate(year.id, {
      onSuccess: () =>
        toast.success(
          t('Current year changed'),
          t('{{name}} is now the current academic year.', { name: year.name }),
        ),
      onError: (e) => toast.error(t('Could not change the current year'), errorMessage(e)),
    })

  return (
    <>
      <PageHeader
        title={t('Academic years')}
        description={t('The school calendar that enrollments, attendance and results belong to.')}
        breadcrumbs={[{ label: t('Academic') }, { label: t('Years') }]}
        homeHref="/admin"
        actions={
          <Can permission="academic.manage">
            <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('New year')}
            </Button>
          </Can>
        }
      />

      {years.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Card key={i} className="space-y-3 p-5">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </Card>
          ))}
        </div>
      ) : years.error ? (
        <Card>
          <ErrorState onRetry={() => void years.refetch()} />
        </Card>
      ) : years.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CalendarRange />}
            title={t('No academic years yet')}
            description={t('Create the current school year before admitting students.')}
            action={
              <Can permission="academic.manage">
                <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
                  {t('New year')}
                </Button>
              </Can>
            }
          />
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {years.data.map((year) => (
            <li key={year.id}>
              <YearCard
                year={year}
                onEdit={() => setEditing(year)}
                onDelete={() => setDeleting(year)}
                onMakeCurrent={() => makeCurrent(year)}
              />
            </li>
          ))}
        </ul>
      )}

      <YearDialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        year={editing === 'new' ? null : editing}
      />
      <DeleteDialog
        item={deleting}
        onClose={() => setDeleting(null)}
        title={(y) => t('Delete {{name}}?', { name: y.name })}
        description={(y) =>
          y.enrollmentCount > 0
            ? t('{{count}} enrollments belong to this year, so it probably cannot be deleted.', {
                count: y.enrollmentCount,
                formatted: formatNumber(y.enrollmentCount),
              })
            : t('The year has no enrollments. This cannot be undone.')
        }
        onDelete={(y) => remove.mutateAsync(y.id)}
        successMessage={(y) => t('{{name}} deleted', { name: y.name })}
      />
    </>
  )
}

interface YearCardProps {
  year: AcademicYear
  onEdit: () => void
  onDelete: () => void
  onMakeCurrent: () => void
}

function YearCard({ year, onEdit, onDelete, onMakeCurrent }: YearCardProps) {
  const { t } = useTranslation('academic')
  const pct = year.isCurrent ? yearProgress(year) : null
  return (
    <Card
      className={cn(
        'flex h-full flex-col p-5 transition-shadow hover:shadow-raised',
        year.isCurrent && 'border-brand-200 ring-1 ring-brand-500/20',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'grid size-10 shrink-0 place-items-center rounded-control [&_svg]:size-5',
            year.isCurrent ? 'bg-brand-gradient text-white shadow-brand' : 'bg-surface-sunken text-muted',
          )}
          aria-hidden="true"
        >
          <CalendarRange />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-base font-semibold">{year.name}</h2>
            {year.isCurrent ? (
              <Badge tone="brand" size="sm" dot>
                {t('Current')}
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-muted">
            {formatDate(year.startsOn)} – {formatDate(year.endsOn)}
          </p>
        </div>
        <Can permission="academic.manage">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                label={t('Actions for {{name}}', { name: year.name })}
                icon={<MoreHorizontal />}
                size="icon-sm"
                className="-mt-1 -mr-2"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem icon={<Pencil />} onSelect={onEdit}>
                {t('Edit')}
              </DropdownMenuItem>
              <DropdownMenuItem icon={<CalendarCheck2 />} onSelect={onMakeCurrent} disabled={year.isCurrent}>
                {t('Set as current')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={<Trash2 />} tone="danger" onSelect={onDelete} disabled={year.isCurrent}>
                {t('Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Can>
      </div>

      <div className="mt-5 flex items-center gap-4 text-sm text-ink-soft">
        <span className="inline-flex items-center gap-1.5">
          <UsersRound className="size-4 text-subtle" aria-hidden="true" />
          <span className="font-medium text-ink tabular">{formatNumber(year.enrollmentCount)}</span>{' '}
          {t('enrollments', { count: year.enrollmentCount })}
        </span>
        <span className="text-muted">
          {t('{{count}} weeks', { count: weeksBetween(year.startsOn, year.endsOn) })}
        </span>
      </div>

      {pct !== null ? (
        <div className="mt-4">
          <div className="flex justify-between text-xs text-muted">
            <span>{t('{{pct}}% complete', { pct: Math.round(pct) })}</span>
          </div>
          <div
            className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-brand-50"
            role="progressbar"
            aria-label={t('{{name}} progress', { name: year.name })}
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="h-full rounded-full bg-brand-600" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : (
        <Can permission="academic.manage">
          <div className="mt-auto pt-4">
            <Button variant="soft" size="sm" leftIcon={<CalendarCheck2 />} onClick={onMakeCurrent}>
              {t('Set as current')}
            </Button>
          </div>
        </Can>
      )}
    </Card>
  )
}
